// Spending limits (boo-backend docs/spend-limits.md §8), in three blocks:
// Organization, Boos, and People (individuals, then budget groups of people).
// Each row is edited in place; no dialogs. Owners, admins and billing admins
// edit and add one-time extras; everyone else sees what the backend's
// visibility filter returns, read-only, under the same blocks.

import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  api,
  ApiError,
  type SpendBooRow,
  type SpendDefaultRow,
  type SpendGroupRow,
  type SpendLimitsPage,
  type SpendOverrideRow,
  type SpendRow,
  type SpendTarget,
} from "@/lib/api";
import {
  capsSummary,
  checkDraft,
  defaultRowOf,
  draftOf,
  firstName,
  fmtCap,
  guestDetail,
  keptOwnText,
  monthResetLabel,
  namesWithRoles,
  PERIOD_WORD,
  targetKey,
  timezoneLabel,
  warnHint,
  warnToSend,
  type Draft,
  type DraftContext,
  type DraftResult,
  type Hint,
  type Period,
} from "@/lib/spend-limits";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SpendGroupMembers } from "./SpendGroupMembers";
import {
  DraftFields,
  FieldLabel,
  Hints,
  LimitRow,
  LINK_BUTTON,
  MoneyInput,
  ROW_GRID,
  SectionHead,
  SUBMIT_DISABLED,
  UNDER_LABEL,
  type RowView,
} from "./SpendLimitRow";
import { SpendPersonPicker } from "./SpendPersonPicker";

const SPEND_LIMITS_KEY = ["billing-spend-limits"] as const;
// The People page links its limit lines to this anchor.
const SPEND_LIMITS_ANCHOR = "spending-limits";

const count = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

function errorText(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error ? err.message : String(err);
}

function ctxFor(row: SpendRow, who: string, object: string, tz: string): DraftContext {
  return {
    who,
    object,
    dailySpent: row.daily.spentUsd,
    monthlySpent: row.monthly.spentUsd,
    dailyExtra: row.daily.extraUsd,
    monthlyExtra: row.monthly.extraUsd,
    monthReset: monthResetLabel(row.monthly.resetsAt, tz),
  };
}

function orgView(r: SpendRow, tz: string): RowView {
  return {
    key: targetKey(r.target),
    row: r,
    name: r.name,
    extraable: true,
    ctx: ctxFor(r, r.name, "every Boo", tz),
  };
}
function booView(r: SpendBooRow, tz: string): RowView {
  return {
    key: targetKey(r.target),
    row: r,
    name: r.name,
    sub: count(r.peopleCount),
    extraable: true,
    ctx: ctxFor(r, r.name, "it", tz),
  };
}
/** What a person's own limit does, shown while adding or editing one. */
function replacesDefaultHint(who: string, def: SpendDefaultRow | null): Hint {
  const summary = def ? capsSummary(def) : null;
  return ["info", `Replaces the default for ${who} (default: ${summary ?? "no limits"}). Leave blank for no limit.`];
}

function defaultView(r: SpendDefaultRow, tz: string, ownUsage: boolean): RowView {
  return {
    key: targetKey(r.target),
    row: r,
    name: "Everyone (default)",
    // A read-only viewer on the default gets their own spend in this row.
    sub: ownUsage ? "Your usage" : `${count(r.appliesTo)} without their own limit`,
    isDefault: !ownUsage,
    ctx: {
      ...ctxFor(r, "", "them", tz),
      defaultRow: { appliesTo: r.appliesTo },
    },
  };
}
function overrideView(r: SpendOverrideRow, tz: string, def: SpendDefaultRow | null): RowView {
  const who = r.person.isGuest ? "this guest" : firstName(r.person.name);
  return {
    key: targetKey(r.target),
    row: r,
    name: r.person.name,
    sub: r.person.isGuest ? (guestDetail(r.person) ?? r.person.guestLabel) : undefined,
    extraable: true,
    ctx: ctxFor(r, firstName(r.person.name), "them", tz),
    editHints: [replacesDefaultHint(who, def)],
  };
}

/** "Pacific Time (Los Angeles)" for America/Los_Angeles; the identifier stays the value. */
function zoneLabel(z: string): string {
  const name = timezoneLabel(z);
  if (name === "UTC") return name;
  const city = z.split("/").pop()!.replace(/_/g, " ");
  return name.includes(city) ? name : `${name} (${city})`;
}

// Offered first in the picker, above a divider, with the viewer's own zone.
const COMMON_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Berlin",
];

function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The reset time zone, for editors (read-only viewers get a sentence in the intro). */
function TimezoneNote({ tz }: { tz: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(tz);
  const zones = useMemo(() => {
    let list: string[];
    try {
      list = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
    } catch {
      list = [];
    }
    if (!list.includes("UTC")) list = ["UTC", ...list];
    if (!list.includes(tz)) list = [tz, ...list];
    const own = browserZone();
    const common = [...new Set([...COMMON_ZONES, ...(own ? [own] : [])])];
    const opt = (z: string) => ({ value: z, label: zoneLabel(z) });
    return {
      common: common.map(opt),
      rest: list.filter((z) => !common.includes(z)).map(opt).sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [tz]);
  const save = useMutation({
    mutationFn: () => api.billing.updateSpendTimezone(value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SPEND_LIMITS_KEY });
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <span className="text-[13px] text-muted-foreground">
        Resets at midnight, {zoneLabel(tz)}
        {" · "}
        <button
          type="button"
          className={LINK_BUTTON}
          onClick={() => {
            setValue(tz);
            setEditing(true);
          }}
        >
          Change
        </button>
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px]">
      <label htmlFor="spend-tz" className="text-muted-foreground">
        Resets at midnight in
      </label>
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger id="spend-tz" size="sm" className="w-72 max-w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {zones.common.map((z) => (
            <SelectItem key={z.value} value={z.value}>
              {z.label}
            </SelectItem>
          ))}
          <SelectSeparator />
          {zones.rest.map((z) => (
            <SelectItem key={z.value} value={z.value}>
              {z.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button disabled={save.isPending || value === tz} onClick={() => save.mutate()}>
        Save
      </Button>
      <Button variant="ghost" className="text-muted-foreground" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      {save.isError && <span className="text-destructive">{errorText(save.error)}</span>}
    </div>
  );
}

/** The org-wide warn %, which every limit without its own level uses. */
function DefaultWarnNote({ percent }: { percent: number }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(percent));
  const [kept, setKept] = useState<string | null>(null);
  const n = Number(value.trim().replace(/%$/, ""));
  const ok = Number.isInteger(n) && n >= 1 && n <= 100;
  const save = useMutation({
    mutationFn: () => api.billing.spendLimits.setDefaultWarn(n),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: SPEND_LIMITS_KEY });
      setKept(keptOwnText(r.ownLevels ?? []));
      setEditing(false);
    },
  });
  useEffect(() => {
    if (!kept) return;
    const t = setTimeout(() => setKept(null), 6000);
    return () => clearTimeout(t);
  }, [kept]);

  if (!editing) {
    return (
      <span className="text-[13px] text-muted-foreground">
        Default warning level: <span className="tabular-nums">{percent}%</span> ·{" "}
        <button
          type="button"
          className={LINK_BUTTON}
          onClick={() => {
            save.reset();
            setKept(null);
            setValue(String(percent));
            setEditing(true);
          }}
        >
          Change
        </button>
        {kept && <span className="ml-2 text-emerald-600 dark:text-emerald-400">Saved. {kept}</span>}
        <span className="block text-xs">Rows with their own level keep it.</span>
      </span>
    );
  }
  const submit = () => ok && n !== percent && !save.isPending && save.mutate();
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-[13px]"
      onKeyDown={(e) => {
        if (e.key === "Escape") setEditing(false);
        if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") submit();
      }}
    >
      <label htmlFor="spend-warn-default" className="text-muted-foreground">
        Default warning level:
      </label>
      <MoneyInput
        id="spend-warn-default"
        value={value}
        bad={!ok}
        describedBy="spend-warn-default-hint"
        autoFocus
        suffix
        className="w-20"
        onChange={setValue}
      />
      <Button disabled={!ok || n === percent || save.isPending} onClick={submit}>
        {save.isPending ? "Saving…" : "Save"}
      </Button>
      <Button variant="ghost" className="text-muted-foreground" onClick={() => setEditing(false)}>
        Cancel
      </Button>
      <span id="spend-warn-default-hint" className={cn("basis-full", ok && !save.isError ? "text-muted-foreground" : "text-destructive")}>
        {!ok
          ? "Pick a warning level between 1% and 100%."
          : save.isError
            ? errorText(save.error)
            : "Rows with their own level keep it."}
      </span>
    </div>
  );
}

type AddState = { section: "groups" | "people"; name: string; draft: Draft };

export function SpendingLimitsSection() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: SPEND_LIMITS_KEY,
    queryFn: api.billing.spendLimits.get,
  });
  const { data: session } = useSession();
  const myUserId = session?.user.id;
  const [editing, setEditing] = useState<{
    key: string;
    draft: Draft;
    name: string;
  } | null>(null);
  const [adding, setAdding] = useState<AddState | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [extraOpen, setExtraOpen] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // The People page links here with #spending-limits; the section renders after
  // its data loads, so scroll once it exists.
  const loaded = query.isSuccess;
  useEffect(() => {
    if (loaded && window.location.hash === `#${SPEND_LIMITS_ANCHOR}`) sectionRef.current?.scrollIntoView({ block: "start" });
  }, [loaded]);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(null), 1800);
    return () => clearTimeout(t);
  }, [saved]);

  // Limit changes also move the lines on every Boo's People page.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: SPEND_LIMITS_KEY }),
      queryClient.invalidateQueries({ queryKey: ["spend-limits-people"] }),
    ]);

  const saveRow = useMutation({
    mutationFn: async (v: { view: RowView; result: DraftResult; rename?: { groupId: string; name: string } }) => {
      if (v.rename) await api.billing.spendLimits.renameGroup(v.rename.groupId, v.rename.name);
      const warnPercent = warnToSend(v.view.row, v.result.warn);
      return api.billing.spendLimits.setLimit({
        target: v.view.row.target,
        dailyUsd: v.result.daily,
        monthlyUsd: v.result.monthly,
        ...(warnPercent !== undefined ? { warnPercent } : {}),
      });
    },
    onSuccess: async (_r, v) => {
      await refresh();
      setEditing(null);
      setSaved(v.view.key);
    },
    // A rename may have saved before the limits failed.
    onError: () => refresh(),
  });

  const removeOwn = useMutation({
    mutationFn: (personKey: string) => api.billing.spendLimits.removePersonLimit(personKey),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
    },
  });

  const deleteGroup = useMutation({
    mutationFn: (groupId: string) => api.billing.spendLimits.deleteGroup(groupId),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setConfirmDelete(null);
    },
  });

  const addExtra = useMutation({
    mutationFn: (v: { view: RowView; period: Period; amountUsd: number }) =>
      api.billing.spendLimits.addExtra({
        target: v.view.row.target,
        period: v.period,
        amountUsd: v.amountUsd,
      }),
    onSuccess: async (_r, v) => {
      await refresh();
      setExtraOpen(null);
      setSaved(v.view.key);
    },
  });
  // Removes every extra on the row for that period, newest first.
  const removeExtra = useMutation({
    mutationFn: async (v: { view: RowView; period: Period }) => {
      const ids = v.view.row.extras
        .filter((e) => e.period === v.period)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((e) => e.id);
      for (const [i, id] of ids.entries()) {
        try {
          await api.billing.spendLimits.removeExtra(id);
        } catch (err) {
          if (i > 0 && err instanceof ApiError && err.code === "extra_in_use") throw new Error("Some of the raise was already spent.", { cause: err });
          throw err;
        }
      }
    },
    onSettled: () => refresh(),
  });

  const removeOneExtra = useMutation({
    mutationFn: (id: string) => api.billing.spendLimits.removeExtra(id),
    onSettled: () => refresh(),
  });

  const addRow = useMutation({
    mutationFn: async (v: { add: AddState; result: DraftResult; defaultWarn: number }) => {
      // Left on the org default unless changed, as an edit does.
      const warnPercent = warnToSend({ warnPercent: v.defaultWarn, warnPercentOwn: null }, v.result.warn);
      const limits = {
        dailyUsd: v.result.daily,
        monthlyUsd: v.result.monthly,
        ...(warnPercent !== undefined ? { warnPercent } : {}),
      };
      if (v.add.section === "groups") {
        const group = await api.billing.spendLimits.createGroup({
          name: v.add.name.trim(),
          ...limits,
        });
        return targetKey({ scope: "group", groupId: group.groupId });
      }
      const target: SpendTarget = { scope: "person", personKey: v.add.name };
      await api.billing.spendLimits.setLimit({ target, ...limits });
      return targetKey(target);
    },
    onSuccess: async (key, v) => {
      await refresh();
      if (v.add.section === "groups") setOpen((o) => ({ ...o, [key]: true }));
      setAdding(null);
      setSaved(key);
    },
  });

  if (query.isLoading) {
    return (
      <section id={SPEND_LIMITS_ANCHOR} aria-label="Spending limits">
        <LoadingIndicator />
      </section>
    );
  }
  if (query.isError || !query.data) {
    return (
      <section id={SPEND_LIMITS_ANCHOR} aria-label="Spending limits">
        <InlineError error={query.error ?? "Failed to load spending limits"} />
      </section>
    );
  }

  const data: SpendLimitsPage = query.data;
  const tz = data.timezone;
  const canEdit = data.viewer.canEdit;
  const defaultWarn = String(data.defaultWarnPercent);
  // Editors always get the default row, even when the backend sends none.
  const personDefault = defaultRowOf(data);

  const startEdit = (view: RowView) => {
    saveRow.reset();
    removeOwn.reset();
    setAdding(null);
    setConfirmDelete(null);
    setExtraOpen(null);
    setEditing({
      key: view.key,
      draft: draftOf(view.row),
      name: typeof view.name === "string" ? view.name : "",
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setConfirmDelete(null);
  };

  const renderRow = (view: RowView, opts?: { group?: SpendGroupRow; override?: SpendOverrideRow }) => {
    const isEditing = editing?.key === view.key;
    const group = opts?.group;
    let nameEditor: ReactNode;
    let extraHints: ReactNode;
    if (isEditing && group) {
      nameEditor = (
        <div className="col-span-2 flex min-w-0 flex-col gap-1 md:col-span-1">
          <FieldLabel htmlFor={`gname-${group.groupId}`}>Group name</FieldLabel>
          <Input
            id={`gname-${group.groupId}`}
            autoFocus
            value={editing.name}
            maxLength={60}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            className="bg-background"
          />
        </div>
      );
      extraHints =
        confirmDelete === group.groupId ? (
          <div className="col-span-full flex flex-wrap items-center gap-2 rounded-lg bg-destructive/5 px-3 py-2 text-[13px]">
            <span>
              Delete {group.name}? {count(group.members.length)} will leave it.
            </span>
            <Button size="sm" variant="destructive" disabled={deleteGroup.isPending} onClick={() => deleteGroup.mutate(group.groupId)}>
              Delete
            </Button>
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="col-span-full text-[13px]">
            <button type="button" className={LINK_BUTTON} onClick={() => setConfirmDelete(group.groupId)}>
              Delete group
            </button>
          </div>
        );
    }
    if (isEditing && opts?.override) {
      const key = opts.override.person.personKey;
      extraHints = (
        <div className="col-span-full text-[13px]">
          <button type="button" className={LINK_BUTTON} disabled={removeOwn.isPending} onClick={() => removeOwn.mutate(key)}>
            Use the default instead
          </button>
        </div>
      );
    }
    const nameTrimmed = editing?.name.trim() ?? "";
    const renameBad = isEditing && group && (nameTrimmed === "" || nameTrimmed.length > 60);
    return (
      <LimitRow
        key={view.key}
        view={view}
        canEdit={canEdit}
        editing={isEditing}
        saved={saved === view.key}
        draft={isEditing ? editing.draft : null}
        onDraft={(draft) => editing && setEditing({ ...editing, draft })}
        onEdit={() => startEdit(view)}
        onCancel={cancelEdit}
        saving={saveRow.isPending}
        error={isEditing ? (renameBad ? "Give the group a name." : errorText(saveRow.error ?? removeOwn.error ?? deleteGroup.error)) : null}
        onSave={(result) => {
          if (renameBad) return;
          saveRow.mutate({
            view,
            result,
            rename: group && nameTrimmed !== group.name ? { groupId: group.groupId, name: nameTrimmed } : undefined,
          });
        }}
        nameEditor={nameEditor}
        extraHints={extraHints}
        extra={{
          open: extraOpen === view.key,
          onOpen: () => {
            addExtra.reset();
            removeExtra.reset();
            setEditing(null);
            setExtraOpen(view.key);
          },
          onClose: () => setExtraOpen(null),
          onAdd: (period, amountUsd) => addExtra.mutate({ view, period, amountUsd }),
          onRemove: (period) => removeExtra.mutate({ view, period }),
          busy:
            (addExtra.isPending && addExtra.variables?.view.key === view.key) ||
            (removeExtra.isPending && removeExtra.variables?.view.key === view.key),
          error: extraOpen === view.key ? errorText(addExtra.error) : null,
          removeError: removeExtra.variables?.view.key === view.key ? errorText(removeExtra.error) : null,
        }}
      />
    );
  };

  const addControls = (section: "groups" | "people") => {
    if (!canEdit) return null;
    if (!adding || adding.section !== section) {
      return (
        <div className="px-4 py-2.5">
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => {
              setEditing(null);
              setExtraOpen(null);
              addRow.reset();
              setAdding({
                section,
                name: "",
                draft: { daily: "", monthly: "", warn: defaultWarn },
              });
            }}
          >
            {section === "groups" ? "+ New group" : "+ Add a person"}
          </Button>
        </div>
      );
    }
    const result = checkDraft(adding.draft, {
      who: "",
      object: "",
      dailySpent: 0,
      monthlySpent: 0,
      dailyExtra: 0,
      monthlyExtra: 0,
      monthReset: "",
    });
    const ownKeys = new Set(data.people.overrides.map((o) => o.person.personKey));
    const candidates = (data.directory ?? []).filter((p) => !ownKeys.has(p.personKey));
    const okName = adding.name.trim() !== "";
    const errs = result.hints.filter(([k]) => k === "err");
    const apiErr = errorText(addRow.error);
    const reasonId = `new-${section}-reason`;
    const reason = okName ? null : section === "groups" ? "Name the group to add it." : "Pick a person to add them.";
    const picked = section === "people" ? candidates.find((p) => p.personKey === adding.name) : undefined;
    const warn = warnHint(adding.draft, result);
    const addHints: Hint[] = [
      ...(apiErr ? [["err", apiErr] as Hint] : []),
      ...errs,
      ...(section === "people"
        ? [replacesDefaultHint(picked ? (picked.isGuest ? "this guest" : firstName(picked.name)) : "this person", personDefault)]
        : []),
      ...(warn ? [warn] : []),
    ];
    const submit = () =>
      okName && result.ok && !addRow.isPending && addRow.mutate({ add: adding, result, defaultWarn: data.defaultWarnPercent });
    return (
      <div
        className={cn(ROW_GRID, "items-start bg-amber-50/70 px-4 py-3 dark:bg-amber-400/[0.06]")}
        onKeyDown={(e) => {
          const el = e.target as HTMLElement;
          // Keys typed in the picker's search belong to the picker.
          if (el.closest("[cmdk-root]")) return;
          if (e.key === "Escape") setAdding(null);
          // Enter submits from any field, the person picker included once someone is picked.
          if (e.key === "Enter" && (el.tagName === "INPUT" || (el.getAttribute("role") === "combobox" && okName))) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <div className="col-span-2 flex min-w-0 flex-col gap-1 md:col-span-1">
          <FieldLabel htmlFor={`new-${section}`}>{section === "groups" ? "Group name" : "Person"}</FieldLabel>
          {section === "groups" ? (
            <Input
              id={`new-${section}`}
              autoFocus
              placeholder="e.g. Sales"
              maxLength={60}
              value={adding.name}
              onChange={(e) => setAdding({ ...adding, name: e.target.value })}
              className="bg-background"
            />
          ) : (
            <SpendPersonPicker
              id={`new-${section}`}
              people={candidates}
              value={adding.name}
              onChange={(name) => setAdding({ ...adding, name })}
              defaultOpen
              focusAfterPick={`new-${section}-daily`}
            />
          )}
        </div>
        <DraftFields
          idPrefix={`new-${section}`}
          draft={adding.draft}
          result={result}
          describedBy={addHints.length ? `new-${section}-hints` : undefined}
          onChange={(draft) => setAdding({ ...adding, draft })}
        />
        <div className={cn("col-span-2 flex flex-wrap items-center justify-end gap-x-1.5 gap-y-1 md:col-span-1", UNDER_LABEL)}>
          <Button
            className={SUBMIT_DISABLED}
            disabled={!okName || !result.ok || addRow.isPending}
            aria-describedby={reason ? reasonId : undefined}
            onClick={submit}
          >
            {addRow.isPending ? "Adding…" : "Add"}
          </Button>
          <Button variant="ghost" className="text-muted-foreground" onClick={() => setAdding(null)}>
            Cancel
          </Button>
          {reason && (
            <span id={reasonId} className="basis-full text-right text-xs text-muted-foreground">
              {reason}
            </span>
          )}
        </div>
        <Hints id={`new-${section}-hints`} hints={addHints} />
      </div>
    );
  };

  const people = data.people;
  // Extras on people who are on the default have no row of their own.
  const ownKeys = new Set(people.overrides.map((o) => o.person.personKey));
  const nameOf = (key: string) => data.directory?.find((p) => p.personKey === key)?.name ?? "Someone";
  const defaultExtras = (data.extras ?? []).filter((e) => e.target.scope === "person" && !ownKeys.has(e.target.personKey));
  const myKey = myUserId ? `u:${myUserId}` : null;
  const ownUsage = !canEdit && !people.overrides.some((o) => o.person.personKey === myKey);
  const showOrg = !!data.org;
  const showBoos = canEdit || data.boos.length > 0;
  const showIndividuals = canEdit || !!personDefault || people.overrides.length > 0;
  const showGroups = canEdit || data.groups.length > 0;

  const individuals = (
    <>
      {personDefault && (
        <div>
          {renderRow(defaultView(personDefault, tz, ownUsage))}
          {canEdit && defaultExtras.length > 0 && (
            <ul className="space-y-0.5 px-4 pb-3 text-xs tabular-nums text-muted-foreground">
              {defaultExtras.map((e) => (
                <li key={e.id}>
                  {e.target.scope === "person" ? nameOf(e.target.personKey) : ""}: raised by {fmtCap(e.amountUsd)} for{" "}
                  {PERIOD_WORD[e.period]} ·{" "}
                  <button
                    type="button"
                    className={LINK_BUTTON}
                    disabled={removeOneExtra.isPending}
                    onClick={() => removeOneExtra.mutate(e.id)}
                  >
                    Undo
                  </button>
                </li>
              ))}
              {removeOneExtra.error && <li className="text-destructive">{errorText(removeOneExtra.error)}</li>}
            </ul>
          )}
        </div>
      )}
      {people.overrides.map((o) => renderRow(overrideView(o, tz, personDefault), { override: o }))}
      {addControls("people")}
    </>
  );

  const groups = (
    <>
      {data.groups.map((g) => {
        const key = targetKey(g.target);
        const isOpen = open[key] ?? g.members.length <= 6;
        const view: RowView = {
          key,
          row: g,
          name: g.name,
          extraable: true,
          shared: true,
          icon: <Users className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />,
          sub: (
            <button
              type="button"
              className="text-left hover:text-foreground"
              aria-expanded={isOpen}
              onClick={() => setOpen((o) => ({ ...o, [key]: !isOpen }))}
            >
              {count(g.members.length)}
              <span aria-hidden> {isOpen ? "▴" : "▾"}</span>
            </button>
          ),
          ctx: ctxFor(g, g.name, "everyone in the group", tz),
        };
        return (
          <div key={key}>
            {renderRow(view, { group: g })}
            {isOpen && <SpendGroupMembers group={g} groups={data.groups} directory={data.directory} canEdit={canEdit} onChanged={refresh} />}
          </div>
        );
      })}
      {addControls("groups")}
    </>
  );

  return (
    <section ref={sectionRef} id={SPEND_LIMITS_ANCHOR} aria-label="Spending limits" className="scroll-mt-6 space-y-8">
      {canEdit ? (
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-x-6 gap-y-1">
            {/* boo-backend docs/spend-limits.md §3.2, §5.2. Who gets warned is on the "Warn at" header. */}
            <p className="text-sm text-pretty text-muted-foreground">
              When a limit is reached, Boo pauses that work until the limit resets or someone raises it.
            </p>
            <button
              type="button"
              className={cn(LINK_BUTTON, "shrink-0 text-[13px]")}
              aria-expanded={settingsOpen}
              aria-controls="spend-limit-settings"
              onClick={() => setSettingsOpen((o) => !o)}
            >
              Limit settings
            </button>
          </div>
          {/* Always rendered (hidden while collapsed) so the toggle's aria-controls points at it. */}
          <div
            id="spend-limit-settings"
            hidden={!settingsOpen}
            className="flex flex-col gap-1.5 rounded-lg border bg-muted/30 px-4 py-3"
          >
            <p className="text-[13px] text-muted-foreground">If more than one limit applies, the first one reached wins.</p>
            <TimezoneNote key={tz} tz={tz} />
            <DefaultWarnNote percent={data.defaultWarnPercent} />
          </div>
        </div>
      ) : (
        <div className="space-y-1 text-sm text-pretty text-muted-foreground">
          <p>When one of these limits is reached, Boo pauses your requests until it resets or an admin raises it.</p>
          {data.viewer.canRaise.length > 0 && <p>Only {namesWithRoles(data.viewer.canRaise)} can change these.</p>}
          <p>Limits reset at midnight {timezoneLabel(tz)}.</p>
        </div>
      )}

      {showOrg && (
        <LimitsBlock id="org" title="Organization">
          <SectionHead />
          {data.org && renderRow(orgView(data.org, tz))}
        </LimitsBlock>
      )}
      {showBoos && (
        <LimitsBlock id="boos" title="Boos">
          <SectionHead />
          <div className="divide-y">{data.boos.map((b) => renderRow(booView(b, tz)))}</div>
        </LimitsBlock>
      )}
      {(showIndividuals || showGroups) && (
        <LimitsBlock id="people" title="People">
          {showIndividuals && (
            <div>
              <SectionHead>Individuals</SectionHead>
              <div className="divide-y">{individuals}</div>
            </div>
          )}
          {showGroups && (
            <div className={showIndividuals ? "border-t" : undefined}>
              <SectionHead>Budget groups</SectionHead>
              <div className="divide-y">{groups}</div>
            </div>
          )}
        </LimitsBlock>
      )}
      {!showOrg && !showBoos && !showIndividuals && !showGroups && (
        <p className="text-sm text-muted-foreground">No spending limits apply to you.</p>
      )}
    </section>
  );
}

/** One of the three top-level blocks: its own heading over its own card. */
function LimitsBlock({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={`spend-${id}-heading`} className="space-y-3">
      <h2 id={`spend-${id}-heading`} className="text-base font-semibold">
        {title}
      </h2>
      <div className="overflow-hidden rounded-lg border bg-card">{children}</div>
    </section>
  );
}
